import { UserManager, WebStorageStateStore, type User as OidcUser } from 'oidc-client-ts';
import { token } from '@/infrastructure/container';
import { authConfig, type AuthConfig } from '@/app/authConfig';
import { ServerError } from '@/domain/errors';

// The signed-in subject, flattened from oidc-client-ts into a shape the ViewModel can display + a fake can
// reproduce. Tokens are surfaced deliberately: this app's whole job is to inspect them.
export interface AuthUser {
  sub: string;
  scopes: string[];
  idToken?: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number; // epoch seconds
  idTokenClaims: Record<string, unknown>;
  accessTokenClaims: Record<string, unknown>;
}

// Everything the tester does against the IdP, behind one interface so the ViewModel is tested against a fake.
export interface IAuth {
  /** Build the client + process a redirect callback if present. Returns the user if signed in. */
  init(): Promise<AuthUser | null>;
  login(): Promise<void>; // navigates away (redirect)
  logout(): Promise<void>; // navigates away (RP-initiated end_session)
  renew(): Promise<AuthUser | null>; // refresh-token silent renew
  currentUser(): Promise<AuthUser | null>;
  introspect(token: string): Promise<Record<string, unknown>>;
  userinfo(accessToken: string): Promise<Record<string, unknown>>;
  discovery(): Promise<Record<string, unknown>>;
  readonly config: AuthConfig;
}

export const TOKEN_IAuth = token<IAuth>('IAuth');

function decodeJwt(jwt: string | undefined): Record<string, unknown> {
  if (!jwt) return {};
  try {
    const payload = jwt.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export class AuthService implements IAuth {
  private readonly um: UserManager;
  readonly config: AuthConfig;

  constructor(config: AuthConfig = authConfig) {
    this.config = config;
    this.um = new UserManager({
      authority: config.authority,
      metadataUrl: config.metadataUrl,
      client_id: config.clientId,
      redirect_uri: window.location.origin,
      post_logout_redirect_uri: window.location.origin,
      response_type: 'code',
      scope: config.scope,
      automaticSilentRenew: true,
      monitorSession: false, // the IdP has no check_session_iframe
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
  }

  async init(): Promise<AuthUser | null> {
    const search = window.location.search;
    if (search.includes('code=') || search.includes('error=')) {
      try {
        const user = await this.um.signinRedirectCallback();
        window.history.replaceState({}, document.title, window.location.pathname);
        return this.toAuthUser(user);
      } catch (cause) {
        throw new ServerError('login callback failed', { cause });
      }
    }
    return this.currentUser();
  }

  async login(): Promise<void> {
    await this.um.signinRedirect();
  }

  async logout(): Promise<void> {
    // Clear local tokens, then RP-initiated logout at the IdP (top-level GET so the SSO cookie is sent and
    // the server-side session is destroyed). On return the store is empty, so the app loads logged out.
    await this.um.removeUser();
    const endSession = await this.um.metadataService.getEndSessionEndpoint();
    const ret = window.location.origin;
    if (endSession) {
      const sep = endSession.includes('?') ? '&' : '?';
      window.location.assign(`${endSession}${sep}post_logout_redirect_uri=${encodeURIComponent(ret)}`);
    } else {
      window.location.assign(ret);
    }
  }

  async renew(): Promise<AuthUser | null> {
    try {
      const user = await this.um.signinSilent();
      return user ? this.toAuthUser(user) : null;
    } catch (cause) {
      throw new ServerError('token renewal failed', { cause });
    }
  }

  async currentUser(): Promise<AuthUser | null> {
    const user = await this.um.getUser();
    return user ? this.toAuthUser(user) : null;
  }

  async introspect(tokenValue: string): Promise<Record<string, unknown>> {
    const md = await this.um.metadataService.getMetadata();
    const endpoint = md.introspection_endpoint;
    if (!endpoint) throw new ServerError('introspection_endpoint not in discovery');
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: tokenValue }),
    });
    return this.asJson(res);
  }

  async userinfo(accessToken: string): Promise<Record<string, unknown>> {
    const md = await this.um.metadataService.getMetadata();
    const endpoint = md.userinfo_endpoint;
    if (!endpoint) throw new ServerError('userinfo_endpoint not in discovery');
    const res = await fetch(endpoint, { headers: { authorization: `Bearer ${accessToken}` } });
    return this.asJson(res);
  }

  async discovery(): Promise<Record<string, unknown>> {
    const md = await this.um.metadataService.getMetadata();
    return md as unknown as Record<string, unknown>;
  }

  private async asJson(res: Response): Promise<Record<string, unknown>> {
    const text = await res.text();
    if (!res.ok) throw new ServerError(`${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch (cause) {
      throw new ServerError('response was not JSON', { cause });
    }
  }

  private toAuthUser(u: OidcUser): AuthUser {
    return {
      sub: String(u.profile.sub ?? ''),
      scopes: u.scopes ?? [],
      idToken: u.id_token,
      accessToken: u.access_token,
      refreshToken: u.refresh_token,
      expiresAt: u.expires_at,
      idTokenClaims: u.profile as Record<string, unknown>,
      accessTokenClaims: decodeJwt(u.access_token),
    };
  }
}

// In-memory fake for ViewModel tests: records calls, returns canned data. No browser, no oidc-client-ts.
export class FakeAuth implements IAuth {
  readonly config: AuthConfig = {
    authority: 'https://idp.test/auth/v1/',
    clientId: 'auth-tester',
    scope: 'openid profile email',
  };
  user: AuthUser | null;
  loginCalls = 0;
  logoutCalls = 0;
  renewCalls = 0;
  introspectResult: Record<string, unknown> = { active: true };
  userinfoResult: Record<string, unknown> = { sub: 'fake-sub' };
  discoveryResult: Record<string, unknown> = { issuer: 'https://idp.test/auth/v1/' };
  failNext: Error | null = null;

  constructor(user: AuthUser | null = null) {
    this.user = user;
  }

  private maybeThrow(): void {
    if (this.failNext) {
      const e = this.failNext;
      this.failNext = null;
      throw e;
    }
  }

  async init(): Promise<AuthUser | null> {
    this.maybeThrow();
    return this.user;
  }
  async login(): Promise<void> {
    this.loginCalls++;
  }
  async logout(): Promise<void> {
    this.logoutCalls++;
    this.user = null;
  }
  async renew(): Promise<AuthUser | null> {
    this.renewCalls++;
    this.maybeThrow();
    return this.user;
  }
  async currentUser(): Promise<AuthUser | null> {
    return this.user;
  }
  async introspect(_token: string): Promise<Record<string, unknown>> {
    this.maybeThrow();
    return this.introspectResult;
  }
  async userinfo(_accessToken: string): Promise<Record<string, unknown>> {
    this.maybeThrow();
    return this.userinfoResult;
  }
  async discovery(): Promise<Record<string, unknown>> {
    this.maybeThrow();
    return this.discoveryResult;
  }
}
